import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  formatCountdown,
  getExamTimeRemainingMs,
  getTimeWarningThresholdMin,
  isLowTime,
} from "@/utils/examTimer";

interface ExamCountdownProps {
  /** Exam start, epoch ms. */
  startTimeMs: number;
  examTimeMinutes: number;
}

/**
 * The remaining-time display for a timed exam.
 *
 * ISOLATED ON PURPOSE. This owns its own interval and state so a tick re-renders only
 * this component. Ticking in `ExamScreen` cascades into `localizedQuestionsByIndex`
 * recomputation and was measured causing 48-69 second INP freezes in long sessions —
 * which is why the exam previously had NO countdown at all. A student sitting a
 * two-hour exam could not see the clock running out.
 *
 * Wall-clock derived (`Date.now()` vs the stored start), not an accumulator, so it stays
 * correct through background-tab throttling and sleep.
 */
export const ExamCountdown = ({
  startTimeMs,
  examTimeMinutes,
}: ExamCountdownProps) => {
  const { t } = useTranslation();
  const [remainingMs, setRemainingMs] = useState<number | null>(() =>
    getExamTimeRemainingMs(startTimeMs, examTimeMinutes, Date.now()),
  );

  useEffect(() => {
    const update = () =>
      setRemainingMs(
        getExamTimeRemainingMs(startTimeMs, examTimeMinutes, Date.now()),
      );

    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [startTimeMs, examTimeMinutes]);

  const low = isLowTime(remainingMs);
  const warningMinutes = getTimeWarningThresholdMin(remainingMs);
  const formattedTime = formatCountdown(remainingMs);
  const displayTime = /^\d+:\d{2}$/.test(formattedTime)
    ? `0:${formattedTime}`
    : formattedTime;

  return (
    <>
      {/* Threshold announcer. The timer itself is aria-live="off" so a screen reader is
          not read the clock every second — which means the low-time state, being a pure
          COLOUR change, was announced to those users never. This says it once per
          threshold crossed. Rendering only the crossed value keeps the live region
          quiet between thresholds. */}
      <span className="sr-only" role="status" aria-live="polite">
        {warningMinutes
          ? t("test.time_warning", { count: warningMinutes, minutes: warningMinutes })
          : ""}
      </span>
    <div
      // `off`, not `polite`: this updates every second, so ANY live region would talk
      // over the question the student is reading. `role="timer"` keeps it reachable on
      // demand via the virtual cursor, which is the right pattern for a long exam —
      // silent by default, readable when asked. The low-time warning is announced
      // separately, at thresholds, by the sr-only region above.
      role="timer"
      aria-live="off"
      aria-label={t("test.time_remaining_aria", {
        time: displayTime,
      })}
      className={cn(
        "flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-base font-semibold leading-6 font-inter tabular-nums",
        low ? "border-[#f5b7b1] bg-[#FFECEB] text-[#C0392B]" : "border-[#dddfe4] bg-[#f4f4f6] text-[#5b616d]",
      )}
    >
      {/* Icon changes with the state, not just the colour: low-time was conveyed by a
          hue shift alone, which is a WCAG 1.4.1 failure for colour-vision deficiency. */}
      {low ? (
        <AlertTriangle className="size-4" aria-hidden="true" />
      ) : null}
      <span>{displayTime}</span>
    </div>
    </>
  );
};

export default ExamCountdown;
